import { useUser } from "@clerk/expo";
import * as ImagePicker from "expo-image-picker";
import { Plus, X } from "lucide-react-native";
import { FC, useState } from "react";

import {
  Button,
  ButtonText,
  HStack,
  Input,
  InputField,
  MediaPicker,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { Question, QuestionDraft, Rule } from "@/types/resources";
import { QuestionSuggestion } from "@/types/responses";
import {
  RulesEditor,
  conditionSourceQuestions,
  hasApplicableRules,
} from "../rules";
import AdvancedSettings from "./AdvancedSettings";
import QuestionField from "./QuestionField";
import QuestionTextInput from "./QuestionTextInput";

const MAX_MEDIA = 10;
const MIN_ITEMS = 2;
const MAX_ITEMS = 10;

interface RankingQuestionEditorProps {
  allQuestions?: Question[];
  currentQuestionOrder?: number;
  disabled?: boolean;
  onChange: (question: QuestionDraft<"ranking">) => void;
  question: QuestionDraft<"ranking">;
}

const RankingQuestionEditor: FC<RankingQuestionEditorProps> = ({
  allQuestions = [],
  currentQuestionOrder,
  disabled = false,
  onChange,
  question,
}) => {
  const { i18n } = useLocalization();
  const { user } = useUser();
  const isAdmin = user?.publicMetadata?.admin === true;
  const [isSuggesting, setIsSuggesting] = useState(false);
  const isDisabled = disabled || isSuggesting;

  const rules = question.rules || [];
  const lines = question.lines || ["", ""];

  const previousConditionQuestions = conditionSourceQuestions(
    allQuestions,
    currentQuestionOrder
  );

  const handleQuestionTextChange = (text: string) => {
    onChange({ ...question, text });
  };

  const handleSuggest = (suggestion: QuestionSuggestion) => {
    const newLines =
      suggestion.lines.length >= MIN_ITEMS ? suggestion.lines : lines;
    onChange({
      ...question,
      lines: newLines,
      options: newLines.map((_, i) => String(i + 1)),
      text: suggestion.text,
    });
  };

  const handleLineChange = (index: number, value: string) => {
    const newLines = [...lines];
    newLines[index] = value;
    onChange({
      ...question,
      lines: newLines,
      options: newLines.map((_, i) => String(i + 1)),
    });
  };

  const addLine = () => {
    if (isAdmin || lines.length < MAX_ITEMS) {
      const newLines = [...lines, ""];
      onChange({
        ...question,
        lines: newLines,
        options: newLines.map((_, i) => String(i + 1)),
      });
    }
  };

  const removeLine = (index: number) => {
    if (lines.length > MIN_ITEMS) {
      const newLines = lines.filter((_, i) => i !== index);
      onChange({
        ...question,
        lines: newLines,
        options: newLines.map((_, i) => String(i + 1)),
      });
    }
  };

  const handleMediaChange = (
    newMedia: (ImagePicker.ImagePickerAsset | string)[]
  ) => {
    onChange({ ...question, media: newMedia });
  };

  const handleRulesChange = (newRules: Rule[]) => {
    onChange({ ...question, rules: newRules });
  };

  return (
    <MediaPicker
      isDisabled={isDisabled}
      maxItems={MAX_MEDIA}
      media={question.media}
      onMediaChange={handleMediaChange}
    >
      <VStack className="gap-6">
        <VStack className="gap-3">
          <QuestionField order={currentQuestionOrder}>
            <QuestionTextInput
              disabled={disabled}
              lines={lines}
              onChange={handleQuestionTextChange}
              onLoadingChange={setIsSuggesting}
              onSuggest={handleSuggest}
              options={[]}
              text={question.text || ""}
              type={question.type}
            />
            <MediaPicker.Trigger compact height={70} width={70} />
          </QuestionField>
          <MediaPicker.Thumbnails />
          <MediaPicker.Error />
        </VStack>

        <VStack className="gap-3">
          <Text className="text-sm font-semibold text-typography-800">
            {i18n.t("questions.ranking.itemsLabel")}
          </Text>
          <VStack className="gap-2 pl-4">
            {lines.map((line, index) => (
              <HStack key={`item-${index}`} className="items-center gap-3">
                <Text className="text-sm text-typography-500 w-6">
                  {index + 1}.
                </Text>
                <Input
                  className="flex-1 border border-outline-200 rounded-xl bg-background-50"
                  isDisabled={isDisabled}
                >
                  <InputField
                    onChangeText={(value) => handleLineChange(index, value)}
                    placeholder={i18n.t("questions.ranking.itemPlaceholder")}
                    value={line}
                  />
                </Input>
                {lines.length > MIN_ITEMS && (
                  <Pressable
                    className="p-1.5 rounded-md hover:bg-error-50"
                    disabled={isDisabled}
                    onPress={() => removeLine(index)}
                  >
                    <X color="rgb(var(--color-typography-500))" size={16} />
                  </Pressable>
                )}
              </HStack>
            ))}
            {(isAdmin || lines.length < MAX_ITEMS) && (
              <Button
                className="self-start rounded-lg border-dashed"
                isDisabled={isDisabled}
                onPress={addLine}
                size="sm"
                variant="outline"
              >
                <Plus color="rgb(var(--color-typography-600))" size={14} />
                <ButtonText className="text-sm text-typography-600 ml-1">
                  {i18n.t("questions.ranking.addItem")}
                </ButtonText>
              </Button>
            )}
          </VStack>
        </VStack>

        {hasApplicableRules(allQuestions, currentQuestionOrder) && (
          <AdvancedSettings defaultExpanded={rules.length > 0}>
            <RulesEditor
              allQuestions={allQuestions}
              currentQuestionOrder={currentQuestionOrder}
              disabled={isDisabled}
              onRulesChange={handleRulesChange}
              previousConditionQuestions={previousConditionQuestions}
              rules={rules}
            />
          </AdvancedSettings>
        )}
      </VStack>
    </MediaPicker>
  );
};

export default RankingQuestionEditor;
